import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

export interface DropdownConfirmProps {
  title: string;
  message: string;
  onConfirm: () => void;
  trigger?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonColor?:
    | 'error'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning';
  minWidth?: number;
  disabled?: boolean;
}

export const DropdownConfirm = ({
  title,
  message,
  onConfirm,
  trigger,
  confirmLabel,
  cancelLabel,
  confirmButtonColor = 'error',
  minWidth = 250,
  disabled = false,
}: DropdownConfirmProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  confirmLabel = confirmLabel || t('common.confirm');
  cancelLabel = cancelLabel || t('common.actions.cancel');

  const handleConfirm = () => {
    onConfirm();
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const defaultTrigger = (
    <IconButton size="small" aria-label={confirmLabel} disabled={disabled}>
      <Icon icon={faTrash} />
    </IconButton>
  );

  return (
    <DropdownMenu
      open={open}
      setOpen={setOpen}
      closeOnContentClick={false}
      trigger={trigger || defaultTrigger}
    >
      <Box p={2} sx={{ minWidth }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {message}
        </Typography>
        <Stack direction="row" spacing={1} mt={2} justifyContent="flex-end">
          <Button size="small" onClick={handleCancel}>
            {cancelLabel}
          </Button>
          <Button
            size="small"
            variant="contained"
            color={confirmButtonColor}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </Stack>
      </Box>
    </DropdownMenu>
  );
};
